import {
  Button,
  Flex,
  Grid,
  GridItem,
  Icon,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AddressCard, Input } from '@shared/components';
import { getCardBrand, links, removeNonNumeric } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { format, parse } from 'date-fns';
import { Trash } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import {
  QUERY_KEY_GET_REQUEST_ALL_INFO,
  useCreateRequestPaymentInput,
} from '../../hooks';
import { CheckoutPaymentSchema, ICheckoutPaymentForm } from '../../schemas';
import {
  getAddressByPostalCode,
  ICreateRequestPaymentInput,
} from '../../services';
import { usePaymentState } from '../../store';

export const PaymentCreditCard = () => {
  const { t } = useTranslation('checkout/payment');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [postalCodePartial, setPostalCodePartial] = useState(false);

  const { paymentForm, setPaymentForm } = usePaymentState();

  const { mutate: createRequestPayment, isLoading } =
    useCreateRequestPaymentInput();

  const hookForm = useForm<ICheckoutPaymentForm>({
    resolver: zodResolver(CheckoutPaymentSchema()),
    shouldFocusError: false,
    defaultValues: paymentForm,
  });

  const handleSubmit = (data: ICheckoutPaymentForm) => {
    const brand = getCardBrand(data.cardNumber);

    const parsedDate = parse(data.expirationDate, 'MM/yy', new Date());
    const formattedExpirationDate = format(
      parsedDate,
      "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
    );

    const payload: ICreateRequestPaymentInput = {
      cardNumber: removeNonNumeric(data.cardNumber),
      confirmationNumber: data.cvv,
      expirationDate: formattedExpirationDate,
      holderName: data.name,
      holderDocument: removeNonNumeric(data.document),
      brand,
      customerAddress: {
        postalCode: removeNonNumeric(data.postalCode),
        complement: data.complement,
        number: data.number,
        street: data.street,
        district: data.district,
        city: data.city,
        state: data.state,
      },
    };

    createRequestPayment(payload, {
      onSuccess: () => {
        setPaymentForm({ ...data, cvv: '' });
        queryClient.invalidateQueries(QUERY_KEY_GET_REQUEST_ALL_INFO);
        navigate(links.checkout.summary);
      },
    });
  };

  const handleGetAddress = async () => {
    const postalCode = hookForm.getValues('postalCode');
    if (postalCode.length === 9) {
      const response = await getAddressByPostalCode(postalCode);

      if (response.erro) {
        hookForm.setError('postalCode', {
          message: t('checkout/payment:postalCodeError'),
        });
      } else {
        hookForm.clearErrors('postalCode');
      }

      if (response.logradouro === '' || response.bairro === '') {
        setPostalCodePartial(true);
      } else {
        setPostalCodePartial(false);
      }

      hookForm.setValue('street', response.logradouro);
      hookForm.setValue('district', response.bairro);
      hookForm.setValue('city', response.localidade);
      hookForm.setValue('state', response.uf);
    }
  };
  const handleClearPostalCode = () => {
    hookForm.setValue('postalCode', '');
    hookForm.setValue('street', '');
    hookForm.setValue('district', '');
    hookForm.setValue('city', '');
    hookForm.setValue('state', '');
    hookForm.setValue('number', '');
    hookForm.setValue('complement', '');
  };
  useEffect(() => {
    handleGetAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookForm.watch('postalCode')]);

  return (
    <FormProvider {...hookForm}>
      <form onSubmit={hookForm.handleSubmit(handleSubmit)}>
        <Flex flexDir="column" gap={4}>
          <Text
            color="brand.800"
            fontSize="2xl"
            fontWeight={600}
            textAlign={{ base: 'center', lg: 'start' }}
          >
            {t('checkout/payment:cardInfos')}
          </Text>
          <Flex
            gap={4}
            h={{ lg: '100dvh' }}
            maxH={{ base: 'calc(100dvh - 186px)', lg: 'calc(100dvh - 214px)' }}
            overflowY="auto"
          >
            <Flex flexDir="column" gap={6} w="full">
              <Grid templateColumns="repeat(4, 1fr)" gap={4} w="full">
                <GridItem colSpan={{ base: 4, lg: 2 }}>
                  <Input
                    label={t('checkout/payment:cardNumber')}
                    name="cardNumber"
                    placeholder={t('checkout/payment:cardNumberPlaceholder')}
                    type="mask"
                    mask="9999 9999 9999 9999"
                  />
                </GridItem>

                <GridItem colSpan={{ base: 2, lg: 1 }}>
                  <Input
                    label={t('checkout/payment:expirationDate')}
                    name="expirationDate"
                    placeholder={t(
                      'checkout/payment:expirationDatePlaceholder',
                    )}
                    type="mask"
                    mask="99/99"
                  />
                </GridItem>
                <GridItem colSpan={{ base: 2, lg: 1 }}>
                  <Input
                    label={t('checkout/payment:cvv')}
                    name="cvv"
                    placeholder={t('checkout/payment:cvvPlaceholder')}
                    type="mask"
                    mask="999"
                  />
                </GridItem>

                <GridItem colSpan={{ base: 4, lg: 2 }}>
                  <Input
                    label={t('checkout/payment:name')}
                    name="name"
                    placeholder={t('checkout/payment:namePlaceholder')}
                  />
                </GridItem>

                <GridItem colSpan={{ base: 4, lg: 2 }}>
                  <Input
                    label={t('checkout/payment:cpf')}
                    name="document"
                    placeholder={t('checkout/payment:cpfPlaceholder')}
                    type="cpf"
                  />
                </GridItem>
              </Grid>

              <Text color="brand.800" fontSize="2xl" fontWeight={600}>
                {t('billingAddress')}
              </Text>

              <Grid templateColumns="repeat(6, 1fr)" gap={4} w="full">
                <GridItem
                  colSpan={{ base: 3, md: 2 }}
                  display="flex"
                  gap={2}
                  alignItems="end"
                >
                  <Input
                    label={t('checkout/payment:postalCode')}
                    name="postalCode"
                    type="mask"
                    mask="99999-999"
                    placeholder={t('checkout/payment:postalCodePlaceholder')}
                  />
                  <IconButton
                    aria-label="Buscar endereço"
                    icon={<Icon as={Trash} />}
                    onClick={handleClearPostalCode}
                  />
                </GridItem>

                <GridItem colSpan={{ base: 2, md: 2 }}>
                  <Input
                    label={t('checkout/payment:number')}
                    name="number"
                    placeholder={t('checkout/payment:numberPlaceholder')}
                  />
                </GridItem>

                <GridItem colSpan={{ base: 6, md: 2 }}>
                  <Input
                    label={t('checkout/payment:complement')}
                    name="complement"
                    placeholder={t('checkout/payment:complementPlaceholder')}
                  />
                </GridItem>

                {postalCodePartial && (
                  <>
                    <GridItem colSpan={{ base: 6, md: 3 }}>
                      <Input
                        label={t('checkout/payment:street')}
                        name="street"
                        placeholder={t('checkout/payment:streetPlaceholder')}
                      />
                    </GridItem>
                    <GridItem colSpan={{ base: 6, md: 3 }}>
                      <Input
                        label={t('checkout/payment:district')}
                        name="district"
                        placeholder={t('checkout/payment:districtPlaceholder')}
                      />
                    </GridItem>
                  </>
                )}
              </Grid>

              {/* caso não haja erro no campo postalCode, exibe o endereço */}
              {hookForm.getValues('city') && (
                <AddressCard
                  city={hookForm.watch('city')}
                  state={hookForm.watch('state')}
                  street={hookForm.watch('street')}
                  district={hookForm.watch('district')}
                  number={hookForm.watch('number')}
                  complement={hookForm.watch('complement')}
                />
              )}

              <Flex
                justifyContent="space-between"
                display={{ base: 'flex', lg: 'none' }}
              >
                <Button
                  as={Link}
                  to={links.checkout.data}
                  colorScheme="brand"
                  variant="outline"
                >
                  {t('common:previous')}
                </Button>

                <Button
                  type="submit"
                  colorScheme="brand"
                  w={140}
                  isLoading={hookForm.formState.isSubmitting || isLoading}
                >
                  {t('common:continue')}
                </Button>
              </Flex>
            </Flex>
          </Flex>

          <Flex
            justifyContent="space-between"
            display={{ base: 'none', lg: 'flex' }}
          >
            <Button
              as={Link}
              to={links.checkout.data}
              colorScheme="brand"
              variant="outline"
            >
              {t('common:previous')}
            </Button>

            <Button
              type="submit"
              colorScheme="brand"
              w={240}
              isLoading={hookForm.formState.isSubmitting || isLoading}
            >
              {t('common:continue')}
            </Button>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};
